import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Select } from './ui/select';

export interface Scope1UploadFormProps {
  companyId?: number;
  onUploaded?: () => void;
}

const formTypes = [
  { 
    value: 'measured', 
    label: 'Measured Form', 
    color: 'text-green-600', 
    description: 'Direct measurement data - actual fuel consumption quantities',
    details: 'Use this for actual fuel consumption data (liters of gasoline, cubic meters of natural gas, etc.)'
  },
  { 
    value: 'estimation_scenario1', 
    label: 'Estimation Scenario 1', 
    color: 'text-blue-600', 
    description: 'Vehicle fuel estimation based on distance and fuel economy',
    details: 'Use this when you have vehicle distance traveled and fuel economy data'
  },
  { 
    value: 'estimation_scenario2', 
    label: 'Estimation Scenario 2', 
    color: 'text-purple-600', 
    description: 'Generator spend estimation based on fuel costs',
    details: 'Use this when you have fuel spending amounts and need to estimate consumption'
  },
];

export function Scope1UploadForm({ companyId, onUploaded }: Scope1UploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [formType, setFormType] = useState('measured');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const feather = (window as any).feather as { replace: (opts?: Record<string, unknown>) => void } | undefined;
    feather?.replace();
  }, [formType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !companyId) return;
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('company_id', String(companyId));
      formData.append('scope', 'scope1');
      formData.append('form_type', formType);

      const token = localStorage.getItem('access_token') || '';
      const res = await fetch('http://localhost:9000/api/upload-emissions', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      onUploaded?.();
      alert('File uploaded and processed successfully.');
    } catch (err: any) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedFormType = formTypes.find(ft => ft.value === formType);

  return (
    <div className="space-y-6">
      {/* Information Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <i data-feather="info" className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"></i>
          <div>
            <h3 className="text-sm font-medium text-blue-800 mb-2">How Data Type Detection Works</h3>
            <div className="text-sm text-blue-700 space-y-2">
              <p><strong>Step 1:</strong> Select the form type below that matches your data</p>
              <p><strong>Step 2:</strong> Download the corresponding CSV template</p>
              <p><strong>Step 3:</strong> Fill in your data using the template format</p>
              <p><strong>Step 4:</strong> Upload your file - the system will automatically:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Detect the data type based on your selection</li>
                <li>Process the data according to the correct method</li>
                <li>Store it with the proper classification (measured/estimated)</li>
                <li>Calculate emissions using the appropriate factors</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Country Codes Reference */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <i data-feather="map-pin" className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0"></i>
          <div>
            <h3 className="text-sm font-medium text-green-800 mb-2">Available Country Codes</h3>
            <div className="text-sm text-green-700">
              <p className="mb-2">Use these country codes in your CSV files:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                <div className="bg-white p-2 rounded border">
                  <strong>KSA</strong> - Kingdom of Saudi Arabia
                </div>
                <div className="bg-white p-2 rounded border">
                  <strong>UAE</strong> - United Arab Emirates
                </div>
                <div className="bg-white p-2 rounded border">
                  <strong>OM</strong> - Oman
                </div>
                <div className="bg-white p-2 rounded border">
                  <strong>QA</strong> - Qatar
                </div>
                <div className="bg-white p-2 rounded border">
                  <strong>BH</strong> - Bahrain
                </div>
                <div className="bg-white p-2 rounded border">
                  <strong>KW</strong> - Kuwait
                </div>
                <div className="bg-white p-2 rounded border">
                  <strong>PK</strong> - Pakistan
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Form Type
            </label>
            <Select value={formType} options={formTypes} onChange={(e: any) => setFormType(e.target ? e.target.value : e)} />
            {selectedFormType && (
              <div className={`mt-3 p-4 rounded-lg border-l-4 ${selectedFormType.color.replace('text-', 'border-')} bg-gray-50`}>
                <div className={`text-sm font-medium ${selectedFormType.color} mb-2`}>
                  <i data-feather="info" className="w-4 h-4 inline mr-1"></i>
                  {selectedFormType.label}
                </div>
                <div className="text-sm text-gray-700 mb-2">
                  {selectedFormType.description}
                </div>
                <div className="text-xs text-gray-600 bg-white p-2 rounded border">
                  <i data-feather="lightbulb" className="w-3 h-3 inline mr-1"></i>
                  {selectedFormType.details}
                </div>
              </div>
            )}
          </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload File
          </label>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Button type="submit" disabled={!file || !companyId || isSubmitting}>
          {isSubmitting ? 'Uploading…' : 'Upload File'}
        </Button>
        <div className="flex flex-col space-y-2">
          <a
            href={`http://localhost:9000/api/download-template?scope=scope1&form_type=${formType}`}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline text-sm inline-flex items-center"
          >
            <i data-feather="download" className="w-4 h-4 inline mr-1"></i>
            Download CSV template for {selectedFormType?.label || 'selected form type'}
          </a>
          <p className="text-xs text-gray-500">
            The template will have the correct columns and format for your selected data type
          </p>
          
          {/* Template File Names */}
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">📋 CSV Template File Names</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <div className="flex items-center">
                <span className="text-green-600 mr-2">🔵</span>
                <span><strong>Scope 1 Templates:</strong></span>
              </div>
              <div className="ml-4 space-y-1">
                <div>• <strong>Measured Activity:</strong> scope1_measured_template.csv</div>
                <div>• <strong>Estimation Scenario 1:</strong> scope1_scenario1_template.csv</div>
                <div>• <strong>Estimation Scenario 2:</strong> scope1_scenario2_template.csv</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </form>
    </div>
  );
}
